import { CSSProperties, useCallback } from 'react';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { GradientIcon } from '../gradients/GradientIcon';
import { useMobile } from 'src/hooks/useMobile';

type VideoBannerControlsProps = {
  activeGameIndex: number;
  numberOfGames: number;
  rotateActiveGame: (n: number) => void;
};

export function VideoBannerControls({
  activeGameIndex,
  numberOfGames,
  rotateActiveGame,
}: VideoBannerControlsProps) {
  const isMobile = useMobile();

  const isActive = useCallback(
    (indicatorIndex: number, gameIndex: number) => {
      const indicatorToGamesRatio = Math.ceil(numberOfGames / 10);
      const numBiggerGroups = numberOfGames % 10 || 10;

      const offset =
        indicatorIndex < numBiggerGroups
          ? 0
          : numBiggerGroups * indicatorToGamesRatio;

      const windowStart =
        indicatorIndex < numBiggerGroups
          ? indicatorIndex
          : indicatorIndex - numBiggerGroups;
      const windowSize =
        indicatorToGamesRatio - (indicatorIndex < numBiggerGroups ? 0 : 1);

      return (
        offset + windowStart * windowSize <= gameIndex &&
        gameIndex < offset + (windowStart + 1) * windowSize
      );
    },
    [numberOfGames],
  );

  const staticIndicatorWrapperCSS: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  };

  const indicatorWrapperCSS: CSSProperties = isMobile
    ? { ...staticIndicatorWrapperCSS, zIndex: '1', position: 'absolute' }
    : staticIndicatorWrapperCSS;

  const staticIndicatorCSS: CSSProperties = {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  };

  const indicatorCSS: CSSProperties = isMobile
    ? { ...staticIndicatorCSS, bottom: '5%' }
    : { ...staticIndicatorCSS, top: '22px' };

  return (
    <>
      <IconButton
        onClick={() => rotateActiveGame(1)}
        sx={{
          position: 'absolute',
          top: 'calc(50% - 32px)',
          zIndex: '2',
          right: 0,
        }}
      >
        <GradientIcon
          gradientColor="primary.verticalInverse"
          sx={{ width: '50px', height: '50px' }}
          IconComponent={ChevronRightRoundedIcon}
        />
      </IconButton>
      <IconButton
        onClick={() => rotateActiveGame(-1)}
        sx={{
          position: 'absolute',
          top: 'calc(50% - 32px)',
          zIndex: '2',
          left: 0,
        }}
      >
        <GradientIcon
          gradientColor="primary.verticalInverse"
          sx={{ width: '50px', height: '50px' }}
          IconComponent={ChevronLeftRoundedIcon}
        />
      </IconButton>
      <Box
        sx={{
          ...indicatorWrapperCSS,
        }}
      >
        <Box
          sx={{
            ...indicatorCSS,
          }}
        >
          {[...new Array(10).keys()].map((index) => (
            <Box
              key={index}
              sx={
                isActive(index, activeGameIndex)
                  ? {
                      width: '12px',
                      height: '12px',
                      border: 'double 1px transparent',
                      borderRadius: '50%',
                      background: '#4DC8FF',
                      boxShadow:
                        '0px 0px 11.9952px #47C5FF, 0px 0px 6.8544px #47C5FF, 0px 0px 3.9984px #47C5FF, 0px 0px 1.9992px #47C5FF, 0px 0px 0.5712px #47C5FF, 0px 0px 0.2856px #47C5FF',
                    }
                  : {
                      width: '12px',
                      height: '12px',
                      border: 'double 1px transparent',
                      borderRadius: '50%',
                      backgroundImage:
                        'linear-gradient(#0064E9, #0064E9), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'content-box, border-box',
                      boxShadow: 4,
                    }
              }
            />
          ))}
        </Box>
      </Box>
    </>
  );
}
